<!-- Filters Editor - manages array of filter objects -->
<script lang="ts">
  import { updateObject } from '../stores/sceneEditorStore';
  import { generateSchemaFromObject } from '../metadata/schemas';
  import Button from '@/ui/base/Button.svelte';
  import Icon from '@/ui/base/Icon.svelte';
  import Checkbox from '@/ui/base/Checkbox.svelte';

  let {
    objectId,
    filters = [],
  }: {
    objectId: string;
    filters: any[];
  } = $props();

  function addFilter() {
    const newFilter = {
      type: 'neonGlowScanline',
      enabled: true,
      glowColor: 0x8b5cf6,
      innerStrength: 0.75,
      outerStrength: 2.5,
      radius: 6.0,
      samples: 12,
      alphaThreshold: 0.01,
      scanIntensity: 0.25,
      scanSpeed: 1.6,
    };

    updateObject(objectId, {
      filters: [...filters, newFilter],
    });
  }

  function removeFilter(filterIndex: number) {
    const newFilters = filters.filter((_: any, index: number) => index !== filterIndex);
    updateObject(objectId, { filters: newFilters });
  }

  function updateFilterProperty(filterIndex: number, property: string, value: any) {
    const newFilters = [...filters];
    newFilters[filterIndex] = { ...newFilters[filterIndex], [property]: value };
    updateObject(objectId, { filters: newFilters });
  }
</script>

<div class="filters-editor space-y-3">
  <div class="flex items-center justify-between">
    <h4 class="text-xs font-medium text-white border-b border-gray-600 pb-1">Filters</h4>
    <Button size="sm" variant="secondary" on:click={addFilter}>
      <Icon name="plus" size="sm" />
      Add Filter
    </Button>
  </div>

  {#if filters.length === 0}
    <div class="text-xs text-gray-500 text-center py-4">No filters applied</div>
  {/if}

  {#each filters as filter, index (index)}
    {@const filterSchema = generateSchemaFromObject(filter)}
    <div class="filter-item bg-gray-800 rounded-lg p-3 space-y-2">
      <div class="flex items-center justify-between mb-2">
        <div class="flex items-center gap-2">
          <span class="text-xs font-medium text-gray-300">{filter.type}</span>
          <Checkbox
            checked={filter.enabled}
            onChange={checked => updateFilterProperty(index, 'enabled', checked)}
          />
          <span class="text-xs text-gray-400">Enabled</span>
        </div>
        <Button size="sm" variant="danger" on:click={() => removeFilter(index)}>
          <Icon name="trash" size="sm" />
        </Button>
      </div>

      <!-- Manual filter properties (without PropertyEditor for now) -->
      <div class="space-y-2">
        {#each filterSchema as component}
          {#each component.properties as property}
            {#if property.key !== 'type' && property.key !== 'enabled'}
              <div class="property-item">
                <div class="text-xs text-gray-400 mb-1">
                  {property.label}
                </div>
                {#if property.type === 'color'}
                  <input
                    type="color"
                    value="#{property.value.toString(16).padStart(6, '0')}"
                    onchange={e => {
                      const target = e.target as HTMLInputElement;
                      if (target) {
                        const hex = target.value.replace('#', '');
                        updateFilterProperty(index, property.key, parseInt(hex, 16));
                      }
                    }}
                    class="w-full h-8 rounded cursor-pointer"
                  />
                {:else if property.type === 'slider'}
                  <div class="flex items-center gap-2">
                    <input
                      type="range"
                      min={property.min}
                      max={property.max}
                      step={property.step}
                      value={property.value}
                      oninput={e => {
                        const target = e.target as HTMLInputElement;
                        if (target)
                          updateFilterProperty(index, property.key, parseFloat(target.value));
                      }}
                      class="flex-1"
                    />
                    <input
                      type="number"
                      min={property.min}
                      max={property.max}
                      step={property.step}
                      value={property.value}
                      oninput={e => {
                        const target = e.target as HTMLInputElement;
                        if (target)
                          updateFilterProperty(index, property.key, parseFloat(target.value));
                      }}
                      class="w-16 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                    />
                  </div>
                {:else if property.type === 'number'}
                  <input
                    type="number"
                    value={property.value}
                    oninput={e => {
                      const target = e.target as HTMLInputElement;
                      if (target)
                        updateFilterProperty(index, property.key, parseFloat(target.value) || 0);
                    }}
                    class="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                  />
                {/if}
              </div>
            {/if}
          {/each}
        {/each}
      </div>
    </div>
  {/each}
</div>

<style>
  .filter-item {
    border: 1px solid rgba(107, 114, 128, 0.3);
    transition: border-color 0.2s;
  }

  .filter-item:hover {
    border-color: rgba(139, 92, 246, 0.5);
  }
</style>
